import React, { memo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { App, Button, Checkbox, Dropdown, Pagination, Table, Tooltip, Typography } from 'antd';

import { formatDate } from 'helpers/date';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Truncate from 'v2/components/Shared/TruncateText';
import type { IAffiliate } from 'v2/interfaces/affiliate';
import affiliateService from 'v2/services/affiliate';

import { Icon } from '@eduzz/ui-antd-theme';

import CustomCommissionForm from './CustomCommissionForm';
import ModalNewMessage from './ModalNewMessage';

interface IProps {
  onSort: (sortField: string, sortDirection: 'asc' | 'desc') => void;
}

const TableApproved = ({ onSort }: IProps) => {
  const { message, modal } = App.useApp();
  const history = useHistory();

  const [isLoading] = useObservable(() => affiliateService.isLoading(), []);
  const [affiliates] = useObservable(() => affiliateService.getAffiliates(), []);
  const [pagination] = useObservable(() => affiliateService.getPaginationResponse(), []);
  const [currentSort] = useObservable(() => affiliateService.getCurrentSort(), []);

  const [affiliateSelected, setAffiliateSelected] = useState<IAffiliate | null>(null);
  const [affiliatesSelected, setAffiliatesSelected] = useState<Array<IAffiliate>>([]);
  const [modalNewMessage, setModalNewMessage] = useState(false);
  const [openModalCommision, setOpenModalCommision] = useState(false);

  const openModalCommission = (affiliate: IAffiliate) => {
    setAffiliateSelected(affiliate);
    setOpenModalCommision(true);
  };

  const closeModalCommission = () => {
    setOpenModalCommision(false);
    setAffiliateSelected(null);
  };

  const toggleModalNewMessage = () => {
    setModalNewMessage(!modalNewMessage);
  };

  const onChangeRowsPerPage = (perPage: number) => {
    affiliateService.setPagination({ ...pagination, ...{ per_page: perPage, current_page: 1 } });
    document.getElementById('affiliates_table')?.scrollIntoView();
  };

  const onChangePage = (page: number) => {
    affiliateService.setPagination({ ...pagination, ...{ current_page: page } });
    document.getElementById('affiliates_table')?.scrollIntoView();
  };

  const resetAffiliatesSelected = () => {
    setAffiliatesSelected([]);
  };

  const selectAllAffiliates = () => {
    const selected = affiliates.map(a => a);

    if (affiliates.length > affiliatesSelected.length) {
      setAffiliatesSelected(selected);
      return;
    }

    setAffiliatesSelected([]);
  };

  const toggleSelect = (affiliate: IAffiliate) => {
    if (affiliatesSelected.filter(a => a.id === affiliate.id).length) {
      setAffiliatesSelected(affiliatesSelected.filter(a => a.id !== affiliate.id));
      return;
    }

    setAffiliatesSelected([...affiliatesSelected, affiliate]);
  };

  const handleClickModerate = (id: number, status: 'A' | 'R') => {
    affiliateService.moderateAffiliate(id, status).subscribe(
      () => {
        setAffiliatesSelected([]);
        message.success('Afiliação cancelada com sucesso.');
      },
      () => message.error('Não foi possível cancelar a afiliação.')
    );
  };

  const repprovedAffiliates = () => {
    modal.confirm({
      title: 'Atenção',
      content: 'Deseja realmente cancelar a afiliação dos afiliados selecionados?',
      okText: 'Cancelar afiliação',
      cancelText: 'Voltar',
      cancelButtonProps: { type: 'text' },
      okButtonProps: { danger: true },
      onOk: () => {
        const affiliateIds = affiliatesSelected.map(affiliate => affiliate.id);
        affiliateService.moderateMultipleAffiliates(affiliateIds, 'R').subscribe(
          () => {
            setAffiliatesSelected([]);
            message.success('Afiliados reprovados com sucesso.');
          },
          () => message.error('Não foi possível cancelar as afiliações.')
        );
      }
    });
  };

  const checkIfAffiliateIsSelect = (affiliateId: number) =>
    !!affiliatesSelected.filter(a => a.id === affiliateId).length;

  const goToMessagesPage = (affiliate: IAffiliate) => {
    history.push(`/producer/messages?affiliate=${affiliate.affiliate_id}`);
  };

  const handleSortClick = (field: string) => {
    const isAsc = currentSort?.field === field && currentSort?.direction === 'asc';
    onSort(field, isAsc ? 'desc' : 'asc');
  };

  const columns: any[] = [
    {
      title: () => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Checkbox
            indeterminate={!!affiliatesSelected?.length && affiliatesSelected?.length < affiliates?.length}
            checked={!!affiliates?.length && affiliatesSelected?.length === affiliates?.length}
            onChange={() => selectAllAffiliates()}
            disabled={!affiliates?.length}
          />
        </PermissionHide>
      ),
      width: 45,
      render: (_: any, affiliate: IAffiliate) => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Checkbox checked={checkIfAffiliateIsSelect(affiliate.id)} onClick={() => toggleSelect(affiliate)} />
        </PermissionHide>
      )
    },
    {
      title: 'Data da solicitação',
      width: 170,
      sorter: true,
      sortOrder:
        currentSort?.field === 'request_date' ? (currentSort?.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (_: any, affiliate: IAffiliate) => (
        <Typography.Text>{formatDate(affiliate.request_date)}</Typography.Text>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('request_date') })
    },
    {
      title: 'Produto',
      width: 250,
      sorter: true,
      sortOrder:
        currentSort?.field === 'product_title' ? (currentSort?.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (_: any, affiliate: IAffiliate) => (
        <div className='flex flex-col items-start gap-1'>
          <Typography.Text strong>
            <Truncate count={30} final='...'>
              {affiliate.product_title}
            </Truncate>
          </Typography.Text>
          <Typography.Text type='secondary'>({affiliate.product_id})</Typography.Text>
        </div>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('product_title') })
    },
    {
      title: 'Afiliado',
      sorter: true,
      sortOrder:
        currentSort?.field === 'affiliate_name' ? (currentSort?.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (_: any, affiliate: IAffiliate) => (
        <div className='flex flex-col items-start gap-1'>
          <Link to={`/producer/detail/${affiliate.affiliate_id}`} target='_blank' rel='noopener noreferrer'>
            <Truncate count={25} final={'...'}>
              {affiliate.affiliate_name}
            </Truncate>
          </Link>
          <Typography.Text type='secondary'>{affiliate.affiliate_email}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('affiliate_name') })
    },
    {
      title: 'F.I',
      width: 50,
      render: (_: any, affiliate: IAffiliate) => <span>{affiliate.funnel_leads}</span>
    },
    {
      title: 'Vendas',
      width: 90,
      render: () => (
        <Tooltip title='Em breve voltaremos a mostrar esses valores'>
          <div className='flex cursor-pointer'>
            Indisponível{' '}
            <span className='m-1 font-normal text-(--uizz-antd-color-text-secondary)'>
              <Icon.CircleHelp size={16} />
            </span>
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Faturamento',
      width: 130,
      render: () => (
        <Tooltip title='Em breve voltaremos a mostrar esses valores'>
          <div className='flex cursor-pointer'>
            Indisponível{' '}
            <span className='m-1 font-normal text-(--uizz-antd-color-text-secondary)'>
              <Icon.CircleHelp size={16} />
            </span>
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Com. diferenciada',
      width: 190,
      render: (_: any, affiliate: IAffiliate) => (
        <span>
          {affiliate.custom_commission === 0 && affiliate.custom_commission_type === null ? (
            'Não configurado'
          ) : (
            <>
              {affiliate.custom_commission_type === 'V' && (
                <FormattedNumber value={affiliate.custom_commission} format='BRL' />
              )}
              {affiliate.custom_commission_type === 'P' && (
                <>
                  <FormattedNumber value={affiliate.custom_commission} />%
                </>
              )}
            </>
          )}
        </span>
      )
    },
    {
      title: 'Ações',
      width: 80,
      fixed: 'right',
      align: 'center' as const,
      render: (_: any, affiliate: IAffiliate) => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Dropdown
            menu={{
              items: [
                { key: 'commission', label: 'Comissão diferenciada', onClick: () => openModalCommission(affiliate) },
                { key: 'message', label: 'Enviar mensagem', onClick: () => goToMessagesPage(affiliate) },
                { key: 'cancel', label: 'Cancelar afiliação', onClick: () => handleClickModerate(affiliate.id, 'R') }
              ]
            }}
            trigger={['click']}
          >
            <Button type='text' shape='circle' icon={<Icon.Ellipsis />} aria-label='Ações' />
          </Dropdown>
        </PermissionHide>
      )
    }
  ];

  return (
    <>
      {!!affiliatesSelected.length && (
        <div className='mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-(--uizz-antd-color-bg-container) p-4 shadow-sm'>
          <Typography.Text strong>{affiliatesSelected.length} selecionado(s)</Typography.Text>
          <div className='flex flex-wrap gap-2'>
            <Button onClick={resetAffiliatesSelected}>Cancelar</Button>
            <Button danger icon={<Icon.ThumbsDown />} onClick={repprovedAffiliates}>
              Cancelar afiliação
            </Button>
            <Button type='primary' icon={<Icon.MessageSquare />} onClick={toggleModalNewMessage}>
              Enviar mensagem
            </Button>
          </div>
        </div>
      )}

      <div>
        <Table
          id='affiliates_table'
          size='small'
          sticky
          className='[&_.ant-table-tbody_.ant-typography]:text-sm'
          scroll={{ x: 'max-content' }}
          columns={columns}
          loading={isLoading}
          dataSource={affiliates || []}
          rowKey='id'
          pagination={false}
          sortDirections={['ascend', 'descend']}
          showSorterTooltip={false}
        />
        {!isLoading && pagination && (
          <div className='mt-4 flex justify-end'>
            <Pagination
              current={pagination.current_page || 1}
              pageSize={pagination.per_page || 10}
              total={pagination.total_items || 0}
              pageSizeOptions={['10', '20', '50', '100', '200']}
              onChange={onChangePage}
              onShowSizeChange={(_current, size) => onChangeRowsPerPage(size)}
              showSizeChanger
            />
          </div>
        )}
      </div>

      <ModalNewMessage
        active={modalNewMessage}
        closeModal={toggleModalNewMessage}
        affiliatesSelected={affiliatesSelected}
      />
      <CustomCommissionForm
        open={openModalCommision}
        handleClose={closeModalCommission}
        affiliate={affiliateSelected}
      />
    </>
  );
};

export default memo(TableApproved);
